import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/components/Toast';
import { useAuth } from '@/context/AuthContext';
import { eventsService } from '@/services/events.service';
import { ATTACHMENT_ACCEPT, getAttachmentContentType } from './attachmentFileTypes';

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/**
 * Leader-only attachment upload (callers gate with RoleGate). Mirrors the
 * expo flow: presigned URL → PUT blob to R2 → record metadata.
 */
export default function AttachmentUploader({
  eventId,
  organizationId,
}: {
  eventId: string;
  /** Pin requests to the event's org (cross-org calendar). */
  organizationId?: string;
}) {
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<string | null>(null);
  const orgId = organizationId ?? currentOrganization?.id;

  const handleFile = async (file: File) => {
    const contentType = getAttachmentContentType(file);

    if (!contentType) {
      showToast('Only PDF, Word, and PowerPoint files are supported.', 'error');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showToast('Files must be 50 MB or smaller.', 'error');
      return;
    }

    try {
      setPhase('Preparing…');
      const upload = await eventsService.getAttachmentUploadUrl(
        eventId,
        file.name,
        contentType,
        file.size,
        organizationId
      );

      setPhase('Uploading…');
      const putResponse = await fetch(upload.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!putResponse.ok) throw new Error(`Upload failed (${putResponse.status})`);

      setPhase('Saving…');
      await eventsService.recordAttachment(
        eventId,
        {
          fileName: upload.fileName,
          originalFileName: upload.originalFileName,
          fileSize: upload.fileSize,
          contentType: upload.contentType,
          storagePath: upload.storagePath,
          downloadUrl: upload.downloadUrl,
        },
        organizationId
      );

      queryClient.invalidateQueries({
        queryKey: [orgId, 'event-attachments', eventId],
      });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      showToast(`${file.name} attached`);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number; data?: { message?: string } } })
        ?.response?.status;
      if (status === 403) {
        showToast(
          (error as { response?: { data?: { message?: string } } }).response?.data?.message ??
            'Attachments are not available on your plan.',
          'error'
        );
      } else {
        showToast("Couldn't upload the file. Try again.", 'error');
      }
    } finally {
      setPhase(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={Boolean(phase)}
        className="flex items-center gap-1.5 rounded-lg border border-dashed border-flock-gray-300 px-3 py-1.5 text-xs font-medium text-flock-navy-500 transition hover:border-flock-navy-400 hover:text-flock-navy-700 disabled:opacity-60"
      >
        <PaperClipIcon className="h-3.5 w-3.5" />
        {phase ?? 'Add attachment'}
      </button>
    </>
  );
}
